import { RESET_STATE } from 'store/global/actions';

import { PRICE_SOURCE_UPLOADS_ACTION_TYPE, PRICE_SOURCES_ACTION_TYPE } from '../actions';

const EMPTY_GRID_ENTRY = { data: null, isLoading: false, hasError: false };

const EMPTY_SUBMIT = {
  isSubmitting: false,
  errorMessage: null,
  isConflict: false,
  conflictCode: null,
  result: null,
};

const EMPTY_APPROVAL = {
  uploadId: null,
  isApproving: false,
  errorMessage: null,
  isConflict: false,
  conflictCode: null,
  result: null,
};

const EMPTY_REVIEW = { data: null, isLoading: false, errorMessage: null };

const EMPTY_OUTLINE = { data: null, isLoading: false, isMissing: false, errorMessage: null };

const detailInitialState = {
  current: { data: null, isLoading: false, hasError: false },
  upload: { isUploading: false, errorMessage: null, duplicateUploadId: null },
  grids: {},
  proposedVersion: {
    data: null,
    isLoading: false,
    errorMessage: null,
    isIncomplete: false,
    isUnreadable: false,
  },
  programCandidates: { data: [], isLoading: false, hasError: false },
  proposalSave: { isSaving: false, errorMessage: null },
  submit: EMPTY_SUBMIT,
  discard: { isDiscarding: false, errorMessage: null },
  review: EMPTY_REVIEW,
  outline: EMPTY_OUTLINE,
};

const initialState = {
  list: { data: [], isLoading: false, hasError: false },
  latest: null,
  approval: EMPTY_APPROVAL,
  ...detailInitialState,
};

const syncListEntry = (list, upload) =>
  upload
    ? {
        ...list,
        data: list.data.map(entry =>
          entry.uploadId === upload.uploadId ? { ...entry, ...upload } : entry,
        ),
      }
    : list;

const setGridEntry = (grids, gridId, entry) => ({ ...grids, [gridId]: entry });

const priceSourceUploadsReducer = (state = initialState, { type, payload }) => {
  switch (type) {
    case PRICE_SOURCE_UPLOADS_ACTION_TYPE.SET_LIST_LOADING:
      return {
        ...state,
        list: {
          ...state.list,
          isLoading: payload,
          hasError: payload ? false : state.list.hasError,
        },
      };

    case PRICE_SOURCE_UPLOADS_ACTION_TYPE.GET_LIST:
      return { ...state, list: { data: payload ?? [], isLoading: false, hasError: false } };

    case PRICE_SOURCE_UPLOADS_ACTION_TYPE.SET_LIST_ERROR:
      return { ...state, list: { ...state.list, isLoading: false, hasError: true } };

    case PRICE_SOURCE_UPLOADS_ACTION_TYPE.SET_LATEST:
      return { ...state, latest: payload ?? null };

    case PRICE_SOURCE_UPLOADS_ACTION_TYPE.SET_CURRENT_LOADING:
      return {
        ...state,
        current: { ...state.current, isLoading: payload, hasError: false },
      };

    case PRICE_SOURCE_UPLOADS_ACTION_TYPE.GET_CURRENT:
      return {
        ...state,
        current: { data: payload ?? null, isLoading: false, hasError: false },
        list: syncListEntry(state.list, payload),
      };

    case PRICE_SOURCE_UPLOADS_ACTION_TYPE.SET_CURRENT_ERROR:
      return { ...state, current: { ...state.current, isLoading: false, hasError: true } };

    case PRICE_SOURCE_UPLOADS_ACTION_TYPE.CLEAR_CURRENT:
      return { ...state, ...detailInitialState };

    case PRICE_SOURCE_UPLOADS_ACTION_TYPE.SET_UPLOADING:
      return {
        ...state,
        upload: { isUploading: payload, errorMessage: null, duplicateUploadId: null },
      };

    case PRICE_SOURCE_UPLOADS_ACTION_TYPE.SET_UPLOAD_ERROR:
      return {
        ...state,
        upload: {
          isUploading: false,
          errorMessage: payload?.message ?? null,
          duplicateUploadId: payload?.duplicateUploadId ?? null,
        },
      };

    case PRICE_SOURCE_UPLOADS_ACTION_TYPE.SET_GRID_LOADING:
      return {
        ...state,
        grids: setGridEntry(state.grids, payload.gridId, { ...EMPTY_GRID_ENTRY, isLoading: true }),
      };

    case PRICE_SOURCE_UPLOADS_ACTION_TYPE.GET_GRID:
      return {
        ...state,
        grids: setGridEntry(state.grids, payload.gridId, {
          ...EMPTY_GRID_ENTRY,
          data: payload.data,
        }),
      };

    case PRICE_SOURCE_UPLOADS_ACTION_TYPE.SET_GRID_ERROR:
      return {
        ...state,
        grids: setGridEntry(state.grids, payload.gridId, { ...EMPTY_GRID_ENTRY, hasError: true }),
      };

    case PRICE_SOURCE_UPLOADS_ACTION_TYPE.SET_PROPOSED_VERSION_LOADING:
      return {
        ...state,
        proposedVersion: {
          data: null,
          isLoading: true,
          errorMessage: null,
          isIncomplete: false,
          isUnreadable: false,
        },
      };

    case PRICE_SOURCE_UPLOADS_ACTION_TYPE.GET_PROPOSED_VERSION:
      return {
        ...state,
        proposedVersion: {
          data: payload ?? null,
          isLoading: false,
          errorMessage: null,
          isIncomplete: false,
          isUnreadable: false,
        },
      };

    case PRICE_SOURCE_UPLOADS_ACTION_TYPE.SET_PROPOSED_VERSION_ERROR:
      return {
        ...state,
        proposedVersion: {
          data: null,
          isLoading: false,
          errorMessage: payload?.message ?? null,
          isIncomplete: !!payload?.isIncomplete,
          isUnreadable: !!payload?.isUnreadable,
        },
      };

    case PRICE_SOURCE_UPLOADS_ACTION_TYPE.SET_PROGRAM_CANDIDATES_LOADING:
      return {
        ...state,
        programCandidates: { ...state.programCandidates, isLoading: true, hasError: false },
      };

    case PRICE_SOURCE_UPLOADS_ACTION_TYPE.GET_PROGRAM_CANDIDATES:
      return {
        ...state,
        programCandidates: { data: payload ?? [], isLoading: false, hasError: false },
      };

    case PRICE_SOURCE_UPLOADS_ACTION_TYPE.SET_PROGRAM_CANDIDATES_ERROR:
      return {
        ...state,
        programCandidates: { ...state.programCandidates, isLoading: false, hasError: true },
      };

    case PRICE_SOURCE_UPLOADS_ACTION_TYPE.SET_PROPOSAL_SAVING:
      return { ...state, proposalSave: { isSaving: payload, errorMessage: null } };

    case PRICE_SOURCE_UPLOADS_ACTION_TYPE.SET_PROPOSAL_ERROR:
      return { ...state, proposalSave: { isSaving: false, errorMessage: payload ?? null } };

    case PRICE_SOURCE_UPLOADS_ACTION_TYPE.SET_SUBMITTING:
      return { ...state, submit: { ...EMPTY_SUBMIT, isSubmitting: true } };

    case PRICE_SOURCE_UPLOADS_ACTION_TYPE.SET_SUBMIT_ERROR:
      return {
        ...state,
        submit: {
          ...EMPTY_SUBMIT,
          errorMessage: payload?.message ?? null,
          isConflict: !!payload?.isConflict,
          conflictCode: payload?.conflictCode ?? null,
        },
      };

    case PRICE_SOURCE_UPLOADS_ACTION_TYPE.SET_SUBMIT_RESULT:
      return { ...state, submit: { ...EMPTY_SUBMIT, result: payload ?? null } };

    case PRICE_SOURCE_UPLOADS_ACTION_TYPE.CLEAR_SUBMIT:
      return { ...state, submit: EMPTY_SUBMIT };

    case PRICE_SOURCE_UPLOADS_ACTION_TYPE.SET_APPROVING:
      return {
        ...state,
        approval: { ...EMPTY_APPROVAL, uploadId: payload.uploadId, isApproving: true },
      };

    case PRICE_SOURCE_UPLOADS_ACTION_TYPE.SET_APPROVE_ERROR:
      return {
        ...state,
        approval: {
          ...EMPTY_APPROVAL,
          uploadId: payload.uploadId,
          errorMessage: payload.message ?? null,
          isConflict: !!payload.isConflict,
          conflictCode: payload.conflictCode ?? null,
        },
      };

    case PRICE_SOURCE_UPLOADS_ACTION_TYPE.SET_APPROVE_RESULT:
      return {
        ...state,
        approval: { ...EMPTY_APPROVAL, uploadId: payload.uploadId, result: payload.result ?? null },
      };

    case PRICE_SOURCE_UPLOADS_ACTION_TYPE.CLEAR_APPROVAL:
      return { ...state, approval: EMPTY_APPROVAL };

    case PRICE_SOURCE_UPLOADS_ACTION_TYPE.SET_DISCARDING:
      return { ...state, discard: { isDiscarding: payload, errorMessage: null } };

    case PRICE_SOURCE_UPLOADS_ACTION_TYPE.SET_DISCARD_ERROR:
      return { ...state, discard: { isDiscarding: false, errorMessage: payload ?? null } };

    case PRICE_SOURCE_UPLOADS_ACTION_TYPE.SET_REVIEW_LOADING:
      return { ...state, review: { ...state.review, isLoading: true, errorMessage: null } };

    case PRICE_SOURCE_UPLOADS_ACTION_TYPE.GET_REVIEW:
      return { ...state, review: { ...EMPTY_REVIEW, data: payload ?? null } };

    case PRICE_SOURCE_UPLOADS_ACTION_TYPE.SET_REVIEW_ERROR:
      return {
        ...state,
        review: { ...state.review, isLoading: false, errorMessage: payload ?? null },
      };

    case PRICE_SOURCE_UPLOADS_ACTION_TYPE.SET_OUTLINE_LOADING:
      return { ...state, outline: { ...state.outline, isLoading: true, errorMessage: null } };

    case PRICE_SOURCE_UPLOADS_ACTION_TYPE.GET_OUTLINE:
      return { ...state, outline: { ...EMPTY_OUTLINE, data: payload ?? null } };

    case PRICE_SOURCE_UPLOADS_ACTION_TYPE.SET_OUTLINE_ERROR:
      return {
        ...state,
        outline: {
          ...state.outline,
          isLoading: false,
          isMissing: !!payload?.isMissing,
          errorMessage: payload?.message ?? null,
        },
      };

    case PRICE_SOURCES_ACTION_TYPE.CLEAR_SOURCE_DETAIL:
    case RESET_STATE:
      return initialState;

    default:
      return state;
  }
};

export default priceSourceUploadsReducer;
